/**
 * OdemeBaglantisiKarti.jsx — where the organizer's PAYMENT LINK is entered (backlog #456, 2026-10-17; backend #455).
 *
 * The link is what an exhibitor is shown when it asks for more electricity than the fair gives free (KwUcretliUyari). Two places mount this card:
 *  · the organizer's own panel (Talepler page), for ITS selected fair, only where SAKA turned the action ODEME_BAGLANTISI_YONET on for the company
 *    (`duzenleyebilir`); otherwise the page lists the closed action with its reason and this card is not drawn;
 *  · the staff Firma Detay › Organizatör Yetkileri card (`personel`): any of the company's fairs, or "every fair" (fair 0), always editable.
 *
 * ⚠️ The link goes to EVERY exhibitor, so whoever edits it can send them anywhere: the backend accepts https only, a domain name (no IP address, user name,
 * password or odd port) and at most 500 characters, writes every change to the history, and lets an organizer change only its own fairs. This card checks the
 * obvious cases before it asks, shows the host of each link, and never makes a link clickable here (it is only text).
 *
 * @param {number}  firmaId          the organizer company
 * @param {number}  [fuarId]         organizer mode: the fair the link is for (the portal's selected fair)
 * @param {boolean} [personel]       staff mode: the scope is chosen here (every fair, or one fair id)
 * @param {boolean} [duzenleyebilir] may the viewer save (staff: always; organizer: the action is open)
 * @param {Function} [onChanged]     `(mesaj)` after a save
 */
import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Input, Label, Spinner, Table } from "reactstrap";
import { Link2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../i18n";
import { getOdemeBaglantisi, setOdemeBaglantisi } from "../../services/portalService";

const URL_EN_FAZLA = 500;
const ETIKET_EN_FAZLA = 100;

function tarih(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
}

/** The obvious refusals, before asking: the backend decides (https, a domain name, no user info, at most 500 characters). Returns the message key or null. */
export function odemeUrlHatasi(url) {
  const u = String(url ?? "").trim();
  if (u === "") return "odeme.urlGerekli";
  if (u.length > URL_EN_FAZLA) return "odeme.urlGecersiz";
  if (!/^https:\/\//i.test(u)) return "odeme.urlHttps";
  try {
    const p = new URL(u);
    if (!p.hostname || !p.hostname.includes(".") || p.username || p.password) return "odeme.urlGecersiz";
  } catch {
    return "odeme.urlGecersiz";
  }
  return null;
}

export default function OdemeBaglantisiKarti({ firmaId, fuarId = null, personel = false, duzenleyebilir = false, onChanged }) {
  const { t } = useTranslation("portal");
  const [liste, setListe] = useState(null);
  const [form, setForm] = useState({ kapsam: personel ? "hepsi" : "fuar", fuar: fuarId ?? "", url: "", etiket: "", aktif: true });
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [hata, setHata] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const out = await getOdemeBaglantisi(firmaId);
        if (!ignore) setListe(out?.baglantilar ?? []);
      } catch (e) {
        if (!ignore) setHata(e.message);
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    if (firmaId) run();
    return () => { ignore = true; };
  }, [firmaId]);

  // the fair this form writes: staff choose, an organizer's is its selected fair
  const hedefFuar = personel ? (form.kapsam === "hepsi" ? 0 : Number(form.fuar)) : Number(fuarId);
  const hedefGecerli = personel ? (form.kapsam === "hepsi" || (Number.isInteger(hedefFuar) && hedefFuar > 0)) : (Number.isInteger(hedefFuar) && hedefFuar > 0);

  function satirdanDoldur(s) {
    setForm({ kapsam: s.fuarId === 0 ? "hepsi" : "fuar", fuar: s.fuarId === 0 ? "" : s.fuarId, url: s.url ?? "", etiket: s.etiket ?? "", aktif: !!s.aktif });
  }

  const urlHatasi = form.url.trim() === "" ? null : odemeUrlHatasi(form.url);

  async function kaydet() {
    setHata(null);
    const h = odemeUrlHatasi(form.url);
    if (h) { setHata(t(h)); return; }
    if (form.etiket.trim().length > ETIKET_EN_FAZLA) { setHata(t("odeme.etiketUzun")); return; }
    setKaydediyor(true);
    try {
      const out = await setOdemeBaglantisi(firmaId, { fuarId: hedefFuar, url: form.url.trim(), etiket: form.etiket.trim() || null, aktif: form.aktif });
      setListe(out?.baglantilar ?? []);
      onChanged?.(t("odeme.kaydedildi"));
    } catch (e) {
      setHata(e.message);
    } finally {
      setKaydediyor(false);
    }
  }

  return (
    <Card className="mb-3" data-testid="odeme-baglantisi-karti">
      <CardHeader className="bg-transparent d-flex align-items-center gap-2">
        <Link2 size={16} className="text-muted" />
        <strong className="flex-grow-1">{t("odeme.baslik")}</strong>
      </CardHeader>
      <CardBody>
        <p className="small text-muted">{t("odeme.aciklama")}</p>
        {hata && <Alert color="danger" className="small py-2" data-testid="odeme-hata">{hata}</Alert>}
        {yukleniyor && !liste && <Spinner size="sm" color="primary" />}
        {liste && (
          <div className="table-responsive mb-3">
            <Table size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr><th>{t("odeme.fuar")}</th><th>{t("odeme.adres")}</th><th>{t("odeme.dugmeYazisi")}</th><th>{t("odeme.durum")}</th><th>{t("odeme.sonDegisiklik")}</th></tr>
              </thead>
              <tbody>
                {liste.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-3">{t("odeme.kayitYok")}</td></tr>}
                {liste.map((s) => (
                  <tr key={s.id ?? `${s.fuarId}`} data-fuar={s.fuarId}
                      role={duzenleyebilir ? "button" : undefined} onClick={duzenleyebilir ? () => satirdanDoldur(s) : undefined}>
                    <td className="small">{s.fuarId === 0 ? t("odeme.tumFuarlar") : `#${s.fuarId}`}</td>
                    {/* the address is TEXT here, never a link: this screen is for editing, the exhibitor's page is where it is clicked */}
                    <td className="small font-monospace" style={{ wordBreak: "break-all" }}>{s.host}<div className="text-muted">{s.url}</div></td>
                    <td className="small">{s.etiket || "—"}</td>
                    <td><Badge color={s.aktif ? "success" : "secondary"}>{s.aktif ? t("odeme.acik") : t("odeme.kapali")}</Badge></td>
                    <td className="small text-muted">{[s.userAdSoyad || (s.userId != null ? `#${s.userId}` : null), tarih(s.tarih)].filter(Boolean).join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {duzenleyebilir && (
          <div data-testid="odeme-formu">
            {personel && (
              <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
                <Label className="small mb-0">{t("odeme.kapsam")}</Label>
                <Input type="select" bsSize="sm" style={{ maxWidth: 180 }} value={form.kapsam} disabled={kaydediyor} data-testid="odeme-kapsam"
                       onChange={(e) => setForm({ ...form, kapsam: e.target.value })}>
                  <option value="hepsi">{t("odeme.tumFuarlar")}</option>
                  <option value="fuar">{t("odeme.tekFuar")}</option>
                </Input>
                {form.kapsam === "fuar" && (
                  <Input type="number" min={1} bsSize="sm" style={{ maxWidth: 120 }} placeholder={t("odeme.fuarNo")} value={form.fuar} disabled={kaydediyor}
                         data-testid="odeme-fuar" onChange={(e) => setForm({ ...form, fuar: e.target.value })} />
                )}
              </div>
            )}
            <Label className="small mb-1" for="odeme-url">{t("odeme.adres")}</Label>
            <Input id="odeme-url" bsSize="sm" className="mb-1" placeholder="https://" maxLength={URL_EN_FAZLA} value={form.url} disabled={kaydediyor}
                   invalid={Boolean(urlHatasi)} data-testid="odeme-url" onChange={(e) => setForm({ ...form, url: e.target.value })} />
            {urlHatasi && <div className="text-danger small mb-1" data-testid="odeme-url-hatasi">{t(urlHatasi)}</div>}
            <Label className="small mb-1 mt-1" for="odeme-etiket">{t("odeme.dugmeYazisi")}</Label>
            <Input id="odeme-etiket" bsSize="sm" className="mb-2" placeholder={t("odeme.odemeYap")} maxLength={ETIKET_EN_FAZLA} value={form.etiket} disabled={kaydediyor}
                   data-testid="odeme-etiket" onChange={(e) => setForm({ ...form, etiket: e.target.value })} />
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="form-check form-switch mb-0">
                <Input type="switch" role="switch" id="odeme-aktif" checked={form.aktif} disabled={kaydediyor} data-testid="odeme-aktif"
                       onChange={(e) => setForm({ ...form, aktif: e.target.checked })} />
                <Label for="odeme-aktif" className="small mb-0">{t("odeme.acik")}</Label>
              </div>
              <Button color="primary" size="sm" disabled={kaydediyor || !hedefGecerli || form.url.trim() === "" || Boolean(urlHatasi)} data-testid="odeme-kaydet" onClick={kaydet}>
                {kaydediyor ? <Spinner size="sm" /> : t("odeme.kaydet")}
              </Button>
            </div>
            <div className="small text-muted mt-2">{t("odeme.guvenlikNotu")}</div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
